// Utility functions for loading and filtering vehicle database
// Phase 9.X: Full Year UI (1998-2026) with year_range filtering
// Section 6: Override system integration

import modelsData from '../database/models.json';
import oilSpecsDataRaw from '../database/oil_specs.json';
import vehicleOverridesData from '../database/vehicles_overrides.json';

// Extract vehicles array from oil_specs.json (handles both array and {vehicles: [...]} format)
const oilSpecsData = Array.isArray(oilSpecsDataRaw) ? oilSpecsDataRaw : (oilSpecsDataRaw?.vehicles || []);

// Override system: Load overrides and merge with master data
const vehicleOverrides = vehicleOverridesData?.overrides || {};

/**
 * Apply overrides to a vehicle spec
 * Merges overridden fields from the override system
 * @param {Object} spec - Original vehicle spec
 * @returns {Object} Spec with overrides applied
 */
function applyOverrides(spec) {
  if (!spec || !spec.id) return spec;

  const override = vehicleOverrides[spec.id];
  if (!override) return spec;

  // Merge overridden fields
  return {
    ...spec,
    ...override.overridden_fields,
    _overridden: true,
    _override_reason: override.reason
  };
}

/**
 * Data Integrity Guard: sanitize fields for production display.
 * Only shows filter_oem and oil_type if verified by an official source.
 * Unverified, quarantined, or contaminated values are hidden (show 'N/A').
 * @param {Object} spec - Vehicle spec object
 * @returns {Object} Sanitized spec safe for production display
 */
function sanitizeForProduction(spec) {
  if (!spec || !spec.specs) return spec;

  const s = spec.specs;
  const sanitizedSpecs = { ...s };

  // Guard filter_oem: must have verified source AND confidence >= 90
  const filterSafe = s.filter_oem_source &&
    (s.filter_oem_confidence === undefined || s.filter_oem_confidence >= 90);
  if (!filterSafe) {
    delete sanitizedSpecs.filter_oem;
  }

  // Guard oil_type: must have verified source AND confidence >= 90
  const oilTypeSafe = s.oil_type_source &&
    (s.oil_type_confidence === undefined || s.oil_type_confidence >= 90);
  if (!oilTypeSafe) {
    delete sanitizedSpecs.oil_type;
  }

  // Guard capacity: show if value exists (basis guard removed — was hiding 12,384 valid records)
  if (!s.oil_capacity_qts) {
    sanitizedSpecs._capacity_verified = false;
  }

  // Aftermarket filters (filter_wix, filter_fram, filter_kn) are quarantined.
  // When rebuilt with authority-verified data, re-enable here.

  return { ...spec, specs: sanitizedSpecs };
}

// Constants for full year range
const YEAR_MIN = 1998;
const YEAR_MAX = 2026;

// Brands hidden from customer-facing VehicleSelector (data kept in DB for admin/reference)
const HIDDEN_MAKES = new Set([
  // Exotic supercars — not serviced by mobile oil change
  'Ferrari', 'Lamborghini', 'Bentley', 'Rolls-Royce', 'Aston Martin',
  'McLaren Automotive', 'Bugatti', 'Maserati', 'Lotus',
  'Koenigsegg', 'Pagani', 'Spyker', 'Morgan', 'Qvale', 'Maybach',
  'RUF Automobile',
  // Ultra-niche — zero US service demand, generate SLA violations with no revenue path
  'Quantum Technologies', 'London Taxi', 'Tecstar LP',
  'Mobility Ventures LLC', 'Mahindra',
]);

/**
 * Get all available categories
 * @returns {Array<{value: string, label: string, description: string}>}
 */
export function getCategories() {
  return [
    {
      value: 'standard',
      label: 'Standard Oil Change',
      description: 'Domestic & Asian vehicles - $125'
    },
    {
      value: 'european',
      label: 'European Oil Change',
      description: 'Premium European vehicles - $150'
    },
    {
      value: 'truck',
      label: 'Diesel/Truck Oil Change',
      description: 'Trucks & large vehicles - $209'
    },
    {
      value: 'rv',
      label: 'RV / Motorhome Oil Change',
      description: 'Class A, B, C Motorhomes - Starting at $175'
    }
  ];
}

/**
 * Get full year range 2003-2026 (for UI dropdown)
 * @returns {number[]} Array of years from 2026 down to 2003
 */
export function getFullYearRange() {
  const years = [];
  for (let year = YEAR_MAX; year >= YEAR_MIN; year--) {
    years.push(year);
  }
  return years;
}

/**
 * Check if a year falls within a year_range string
 * @param {number} year - The year to check
 * @param {string} yearRange - Year range in format "2010-2020" or "2010-2014,2017-2023"
 * @returns {boolean} True if year is in range
 */
// eslint-disable-next-line no-unused-vars
function isYearInRange(year, yearRange) {
  if (!yearRange) return true; // No range = valid for all years

  const yearInt = parseInt(year);

  // Handle multiple ranges separated by commas (e.g., "2006-2014,2017-2023")
  const ranges = yearRange.split(',');

  for (const range of ranges) {
    const trimmedRange = range.trim();

    // Handle single year (e.g., "2015")
    if (!trimmedRange.includes('-')) {
      if (yearInt === parseInt(trimmedRange)) {
        return true;
      }
      continue;
    }

    // Handle year range (e.g., "2010-2020")
    const [startYear, endYear] = trimmedRange.split('-').map(y => parseInt(y.trim()));
    if (yearInt >= startYear && yearInt <= endYear) {
      return true;
    }
  }

  return false;
}

/**
 * Get all makes available for a given year
 * @param {number} year - The selected year (customer's vehicle year)
 * @param {string} category - Optional category filter ('standard', 'european', 'truck')
 * @returns {string[]} Sorted array of makes
 */
export function getMakesForYear(year, category = null) {
  if (!year) return [];

  const yearInt = parseInt(year);

  // Filter from oilSpecsData by exact year match, excluding exotic brands
  // RV records excluded by default unless category='rv' is explicitly requested
  let filteredSpecs = oilSpecsData.filter(spec =>
    spec.year === yearInt && !HIDDEN_MAKES.has(spec.make) &&
    (category === 'rv' ? spec.category === 'rv' : spec.category !== 'rv')
  );

  // Filter by category if provided (use 'category' field, not 'brand_category')
  if (category && category !== 'rv') {
    filteredSpecs = filteredSpecs.filter(spec => spec.category === category);
  }

  const makes = [...new Set(filteredSpecs.map(spec => spec.make))];
  return makes.sort();
}

/**
 * Get all models available for a given year and make
 * @param {number} year - The selected year
 * @param {string} make - The selected make
 * @returns {string[]} Sorted array of models
 */
export function getModelsForYearAndMake(year, make) {
  if (!year || !make) return [];

  const yearInt = parseInt(year);

  const filteredSpecs = oilSpecsData.filter(spec =>
    spec.make === make && spec.year === yearInt
  );

  const models = [...new Set(filteredSpecs.map(spec => spec.model))];
  return models.sort();
}

/**
 * Get all engine options for a given year, make, and model
 * @param {number} year - The selected year (customer's vehicle year)
 * @param {string} make - The selected make
 * @param {string} model - The selected model
 * @returns {Array<{code: string, label: string}>} Array of engine options
 */
export function getEnginesForVehicle(year, make, model) {
  if (!year || !make || !model) return [];

  const yearInt = parseInt(year);

  // Get all engines from oilSpecsData for this make/model/year
  const filteredSpecs = oilSpecsData.filter(spec =>
    spec.make === make && spec.model === model && spec.year === yearInt
  );

  // Build engine list from specs using 'engine' field
  const allEngines = filteredSpecs.map(spec => {
    const engineLabel = spec.engine || 'Unknown Engine';

    return {
      code: spec.engine, // Use engine as code since there's no separate engine_code
      label: engineLabel
    };
  });

  // Remove duplicates by engine label
  const uniqueEngines = Array.from(
    new Map(allEngines.map(e => [e.code, e])).values()
  );

  return uniqueEngines;
}

/**
 * Get oil specifications for a specific vehicle configuration
 * @param {number} year - The vehicle year
 * @param {string} make - The vehicle make
 * @param {string} model - The vehicle model
 * @param {string} engine - The engine description
 * @returns {Object|null} Oil specs object or null if not found
 */
export function getOilSpecs(year, make, model, engine) {
  if (!year || !make || !model || !engine) return null;

  const yearInt = parseInt(year);

  // Find spec that matches make/model/engine/year
  const spec = oilSpecsData.find(s =>
    s.make === make &&
    s.model === model &&
    s.engine === engine &&
    s.year === yearInt
  );

  // Apply overrides, then sanitize for production safety
  const result = spec ? sanitizeForProduction(applyOverrides(spec)) : null;

  // Add computed filters property (backward compatible)
  if (result && result.specs) {
    result.specs.filters = {
      oem: result.specs.filter_oem || null,
      wix: result.specs.filter_wix || null,
      fram: result.specs.filter_fram || null,
      kn: result.specs.filter_kn || null,
    };
  }

  return result;
}

/**
 * Check if the vehicle is electric (no oil change needed)
 * @param {string} engineCode - The engine code
 * @returns {boolean} True if electric vehicle
 */
export function isElectricVehicle(engineCode) {
  return engineCode === 'ELECTRIC';
}

/**
 * High-risk model detection for booking verification.
 * These models have filter changes across years — wrong year/engine = wrong filter.
 *
 * @param {number} year
 * @param {string} make
 * @param {string} model
 * @returns {{ isHighRisk: boolean, reason: string|null, requiresEngineConfirm: boolean }}
 */
export function getFilterRiskWarning(year, make, model) {
  const yearInt = parseInt(year);
  if (!yearInt || !make) return { isHighRisk: false, reason: null, requiresEngineConfirm: false };

  // Toyota 2017+: spin-on vs cartridge transition across most models
  if (make === 'Toyota' && yearInt >= 2017) {
    return {
      isHighRisk: true,
      reason: 'Toyota changed filter types (spin-on vs cartridge) for many models between 2017-2024. Please confirm your engine size to ensure we bring the correct filter.',
      requiresEngineConfirm: true,
    };
  }

  // Lexus 2017+: same Toyota platform issue
  if (make === 'Lexus' && yearInt >= 2017) {
    return {
      isHighRisk: true,
      reason: 'Lexus shares Toyota platforms — filter types changed between 2017-2024. Please confirm your engine size.',
      requiresEngineConfirm: true,
    };
  }

  // Ford high-risk models
  const fordHighRisk = ['Explorer', 'Mustang', 'F-150', 'F-250', 'F-350', 'Escape', 'Ranger'];
  if (make === 'Ford' && model && fordHighRisk.some(m => model.toLowerCase().includes(m.toLowerCase()))) {
    return {
      isHighRisk: true,
      reason: `Ford ${model} uses different oil filters depending on engine option. Please double-check your engine size.`,
      requiresEngineConfirm: true,
    };
  }

  // GM 2018+: PF48E → 19385578 transition
  const gmMakes = ['Chevrolet', 'GMC', 'Buick', 'Cadillac'];
  if (gmMakes.includes(make) && yearInt >= 2018) {
    return {
      isHighRisk: true,
      reason: 'GM updated its standard oil filter in 2018+. Please confirm your engine to ensure we bring the right one.',
      requiresEngineConfirm: true,
    };
  }

  // Hyundai/Kia high-traffic models with multiple filter eras
  const hkHighRisk = ['Tucson', 'Santa Fe', 'Sonata', 'Sorento', 'Palisade'];
  if ((make === 'Hyundai' || make === 'Kia') && model && hkHighRisk.some(m => model.toLowerCase().includes(m.toLowerCase()))) {
    return {
      isHighRisk: true,
      reason: `${make} ${model} uses different filters depending on the year and engine option. Please confirm your engine size.`,
      requiresEngineConfirm: true,
    };
  }

  return { isHighRisk: false, reason: null, requiresEngineConfirm: false };
}

/**
 * Get available years from database (legacy function, use getFullYearRange for UI)
 * @deprecated Use getFullYearRange() for UI dropdowns
 */
export function getAvailableYears(category = null) {
  let filteredModels = modelsData;

  if (category) {
    filteredModels = modelsData.filter(model => model.brand_category === category);
  }

  const years = [...new Set(filteredModels.map(model => model.year))];
  return years.sort((a, b) => b - a);
}
